import React, { useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../../lib/utils';

/**
 * Shared collapsible section card for FSM ticket view pages.
 */
export function FsmCollapsibleCard({
  title,
  subtitle,
  headerRight,
  defaultOpen = true,
  open: openProp,
  onOpenChange,
  className,
  bodyClassName,
  children
}) {
  const contentId = useId();
  const isControlled = openProp !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = isControlled ? openProp : uncontrolledOpen;

  const setOpen = (next) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  return (
    <div
      className={cn(
        'bg-white rounded-2xl shadow-[0_10px_30px_-18px_rgba(30,51,131,0.22)] border border-(--color-text-border)/60 form-surface overflow-hidden',
        className
      )}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen(!open)}
        className={cn(
          'w-full flex items-start sm:items-center justify-between gap-3 text-left px-5 py-4 cursor-pointer',
          'hover:bg-workspace/30 transition-colors',
          open && 'border-b border-gray-100'
        )}>
        <div className="min-w-0 flex-1">
          <h2 className="text-[14px] font-bold text-foreground">{title}</h2>
          {subtitle ?
            <p className="text-[11px] text-gray-400 font-medium mt-0.5">{subtitle}</p> :
            null}
        </div>
        {headerRight ?
          <div
            className="flex items-center gap-2 flex-wrap shrink-0"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}>
            {headerRight}
          </div> :
          null}
        <span
          className={cn(
            'h-8 w-8 shrink-0 grid place-items-center rounded-xl border border-gray-100 bg-workspace/40 text-gray-500 transition-transform duration-200',
            open && 'rotate-180'
          )}
          aria-hidden="true">
          <ChevronDown className="h-4 w-4" />
        </span>
      </button>

      <div
        id={contentId}
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-out',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}>
        <div className="min-h-0 overflow-hidden">
          <div className={cn('px-5 pb-5 pt-4', bodyClassName)}>{children}</div>
        </div>
      </div>
    </div>
  );
}
